import { useMemo, useState } from 'react';
import { FeatureFlagName } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import {
  EnrollmentStatus,
  SidePanelParentPetDataFragment,
  useEnsurePetsArePromotedMutation,
  useGetClinicPetEnrollmentsByParentQuery,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from '../../../../../../../enums/GraphQLFetchPolicies';
import { isEnrollmentConsideredActive } from '../utils/careEnrollments';

/**
 * Used to retrieve a list of pets and the plans that they are enrolled in
 * @param clinicPetParentId
 * @returns PetWithPlan types
 */
export const usePets = (
  clinicPetParentId?: string,
  preSelectedPetId?: string | null,
): {
  pets: SidePanelParentPetDataFragment[];
  isTestPetParent: boolean;
  selectedPet?: SidePanelParentPetDataFragment;
  organizationPetParentId?: string | null;
  select: (clinicPetId: string) => void;
  refetchPets: () => void;
} => {
  const { currentClinicId } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();

  // The backend hook
  const { data: clinicPetParentWithPets, refetch: refetchPets } = useGetClinicPetEnrollmentsByParentQuery({
    variables: {
      where: {
        id: clinicPetParentId || '',
      },
      clinicId: currentClinicId || '',
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !clinicPetParentId || !currentClinicId,
  });

  const [ensurePetsArePromoted] = useEnsurePetsArePromotedMutation();

  // Mapping in to our view model type
  const pets = useMemo(() => {
    const parentPets = clinicPetParentWithPets?.findUniqueClinicPetParent?.pets || [];

    if (isFeatureEnabled(FeatureFlagName.CarePlans)) {
      const petsWithoutOrganizationId = parentPets?.filter((pet) => !pet.organizationPet?.id) || [];

      if (!!petsWithoutOrganizationId.length && clinicPetParentId) {
        ensurePetsArePromoted({
          variables: {
            data: {
              clinicPetParentId,
            },
          },
          onCompleted: () => {
            refetchPets();
          },
        });
      }
    }

    // Only return inactive/deceased pets in this list if they are enrolled in a plan
    return parentPets.filter((pet) => {
      if (pet.computedIsActive && !pet.computedIsDeceased) {
        return pet;
      } else if (pet.organizationPet?.carePlanEnrollments.length) {
        const hasBeenEnrolled = pet.organizationPet?.carePlanEnrollments.find(
          (enrollment) =>
            enrollment.status === EnrollmentStatus.Active || enrollment.status === EnrollmentStatus.Expired,
        );
        if (hasBeenEnrolled) {
          return pet;
        }
      }
    });
  }, [
    clinicPetParentId,
    clinicPetParentWithPets?.findUniqueClinicPetParent?.pets,
    ensurePetsArePromoted,
    isFeatureEnabled,
    refetchPets,
  ]);

  const sortedPets = useMemo(() => {
    return pets.sort((a, b) => {
      const aEnrollment = a.organizationPet?.carePlanEnrollments.find((enrollment) =>
        isEnrollmentConsideredActive(enrollment),
      );
      const bEnrollment = b.organizationPet?.carePlanEnrollments.find((enrollment) =>
        isEnrollmentConsideredActive(enrollment),
      );

      if (aEnrollment && bEnrollment) {
        return aEnrollment.status === EnrollmentStatus.Active ? -1 : 1;
      }

      if (aEnrollment || bEnrollment) {
        return aEnrollment ? -1 : 1;
      }

      return a.name.localeCompare(b.name);
    });
  }, [pets]);

  const petWithPlan = sortedPets[0];

  const [selectedPetId, setSelectedPetId] = useState<string | null>(
    preSelectedPetId ? preSelectedPetId : petWithPlan?.id ?? null,
  );

  // If the selectedPet is not in the pets array, select the first one by default
  const selectedPet = useMemo(() => {
    return sortedPets.find((pet) => pet.id === selectedPetId) || petWithPlan;
  }, [sortedPets, petWithPlan, selectedPetId]);

  return {
    pets: sortedPets,
    isTestPetParent: !!clinicPetParentWithPets?.findUniqueClinicPetParent?.isTest,
    selectedPet,
    organizationPetParentId: clinicPetParentWithPets?.findUniqueClinicPetParent?.organizationPetParentId,
    select: (clinicPetId: string): void => setSelectedPetId(clinicPetId),
    refetchPets,
  };
};
